import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import styles from "./Pricing.module.css";

const RazorpayCheckout = ({
  cost,
  planName,
  planCode,
  userLimit = null,
  appliedPromo = null
}) => {
  const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;
  console.log("userLimit", userLimit);
  console.log("Applied Promo:", appliedPromo);
  console.log("Plan Code:", planCode); // Add this to see what's being passed

  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const token = localStorage.getItem("token");

  useEffect(() => {
    console.log("[RazorpayCheckout] Mounted with cost:", cost, "planCode:", planCode);
  }, [cost, planCode]);

  // Auto-hide error popup after 5 seconds
  useEffect(() => {
    if (showErrorPopup) {
      const timer = setTimeout(() => {
        setShowErrorPopup(false);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showErrorPopup]);

  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      if (window.Razorpay) {
        console.log("[RazorpayCheckout] Razorpay already loaded.");
        return resolve(true);
      }

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => {
        console.log("[RazorpayCheckout] Razorpay script loaded.");
        resolve(true);
      };
      script.onerror = () => {
        console.error("[RazorpayCheckout] Failed to load Razorpay SDK.");
        resolve(false);
      };
      document.body.appendChild(script);
    });

  const handlePayment = async () => {
    console.log("[RazorpayCheckout] handlePayment triggered for planCode:", planCode);

    if (!cost || isNaN(cost)) {
      setError("Invalid cost value.");
      setShowErrorPopup(true);
      return;
    }

    setLoading(true);
    setError(null);
    setShowErrorPopup(false);

    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        setError("Razorpay SDK failed to load. Please refresh the page.");
        setShowErrorPopup(true);
        return;
      }

      console.log("[RazorpayCheckout] Creating order...");

      const order_payload = {
        amount: cost,
        currency: "INR",
        planCode
      };

      // Add promo code to payload if applied
      if (appliedPromo && appliedPromo.valid) {
        order_payload.promo_code = appliedPromo.promo_code;
        console.log("[RazorpayCheckout] Adding promo code to order:", appliedPromo.promo_code);
      }

      if (userLimit) {
        order_payload.userLimit = userLimit;
      }

      const createOrderRes = await fetch(
        `https://mgmt-util2.${backend_api_base_url}/add_payment?action=order`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify(order_payload),
        }
      );

      const responseData = await createOrderRes.json();
      console.log("[RazorpayCheckout] Order response:", responseData);

      if (!createOrderRes.ok || !responseData.success) {
        // Handle error response
        const errorMessage = responseData.message || "Failed to create order";
        setError(errorMessage);
        setShowErrorPopup(true);
        return;
      }

      const orderData = responseData.data;
      console.log("[RazorpayCheckout] Order data:", orderData);

      const options = {
        key: process.env.REACT_APP_RZP_CK,
        amount: parseInt(cost) * 100,
        currency: "INR",
        name: "Sageion.com Checkout",
        description: planName,
        order_id: orderData.razorpay_order_id,
        handler: async (response) => {
          console.log("[RazorpayCheckout] Payment success response:", response);

          const payment_payload = {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            planCode,
          };

          // Include promo code in payment verification if it was used
          if (orderData.promo_code) {
            payment_payload.promo_code = orderData.promo_code;
          }

          if (userLimit) {
            payment_payload.userLimit = userLimit;
          }

          const verifyRes = await fetch(
            `https://mgmt-util2.${backend_api_base_url}/add_payment?action=payment`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: token,
              },
              body: JSON.stringify(payment_payload),
            }
          );

          const verifyData = await verifyRes.json();
          console.log("[RazorpayCheckout] Verify result:", verifyData);

          if (verifyData.success) {
            // Clear promo code from localStorage if it was used
            localStorage.removeItem("appliedPromo");
            // For top-ups, stay on the same page (don't redirect to profile)
            // Check if it's a top-up by planCode
            if (planCode === "TOPUP" || planCode === "INGESTION_TOPUP") {
              // Refresh the page to show updated credits
              window.location.reload();
            } else {
              history.push("/my_profile");
            }
          } else {
            setError("Payment verification failed: " + (verifyData.message || "Unknown error"));
            setShowErrorPopup(true);
          }
        },
        modal: {
          ondismiss: () => {
            console.log("[RazorpayCheckout] Checkout closed");
            setLoading(false);
          }
        },
        theme: {
          color: "#3399cc",
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

      rzp.on("payment.failed", function (response) {
        console.error("[RazorpayCheckout] Payment failed:", response.error);
        setError("Payment failed: " + response.error.description);
        setShowErrorPopup(true);
        setLoading(false);
      });
    } catch (err) {
      console.error("[RazorpayCheckout] Error in handlePayment:", err);
      setError("Error initiating payment: " + err.message);
      setShowErrorPopup(true);
    } finally {
      setLoading(false);
    }
  };

  // Determine button text
  const getButtonText = () => {
    if (loading) return "Processing...";
    if (appliedPromo && appliedPromo.valid) {
      return `Pay ₹${cost.toLocaleString('en-IN')} (with promo)`;
    }
    return `Pay ₹${cost.toLocaleString('en-IN')} with Razorpay`;
  };

  // Determine if this is a top-up
  const isTopUp = planCode === "TOPUP" || planCode === "INGESTION_TOPUP";

  // Error Popup Component
  const ErrorPopup = () => (
    <div className={styles.errorPopup}>
      <div className={styles.errorPopupContent}>
        <span className={styles.errorIcon}>❌</span>
        <div className={styles.errorMessage}>{error}</div>
        <button
          className={styles.errorCloseBtn}
          onClick={() => {
            setShowErrorPopup(false);
            setError(null);
          }}
        >
          ×
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: "300px", margin: "2rem auto", zIndex: 5, position: "relative" }}>
      <h3>{isTopUp ? "Complete Payment" : "Subscribe"}</h3>

      {/* Error Popup */}
      {showErrorPopup && error && <ErrorPopup />}

      {/* Promo Applied Banner */}
      {appliedPromo && appliedPromo.valid && (
        <div style={{
          backgroundColor: "#e6f7e6",
          border: "1px solid #4caf50",
          borderRadius: "4px",
          padding: "8px",
          marginBottom: "10px",
          fontSize: "14px",
          color: "#2e7d32",
          textAlign: "center"
        }}>
          Promo {appliedPromo.promo_code} applied! You save ₹
          {(appliedPromo.original_amount - appliedPromo.discounted_amount).toLocaleString('en-IN')}
        </div>
      )}

      <button
        style={{
          padding: "12px 20px",
          fontSize: "16px",
          cursor: loading ? "wait" : "pointer",
          zIndex: 10,
          position: "relative",
          backgroundColor: appliedPromo ? "#4caf50" : "#3399cc",
          color: "white",
          border: "none",
          borderRadius: "4px",
          width: "100%",
          opacity: loading ? 0.7 : 1,
          transition: "all 0.3s ease"
        }}
        onClick={(e) => {
          e.preventDefault();
          console.log("[RazorpayCheckout] Pay button clicked");
          handlePayment();
        }}
        disabled={loading || !cost}
      >
        {getButtonText()}
      </button>
    </div>
  );
};

export default RazorpayCheckout;